import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './BeltScales.css';
import { useProducts, type BeltScalesSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const BeltScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: models, loading, error } = useProducts<BeltScalesSpecs>('belt-scales', editMode);
  const navigate = useNavigate();

  const specifications = [
    { label: 'Dimensiones disponibles', value: '36" a 60" (91cm a 152cm)' },
    { label: 'Capacidades', value: '25 kg, 50 kg, 100 kg, 250 kg, 500 kg, 1,000 kg' },
    { label: 'Velocidad transportador', value: '30-220 pies/minuto' },
    { label: 'Construcción', value: 'Acero inoxidable o al carbón' },
    { label: 'Banda', value: 'Malla polimérica aprobada USDA' },
    { label: 'Altura ajustable', value: '18" a 45"' },
    { label: 'Productividad', value: 'Hasta 20 productos/minuto' },
    { label: 'Memoria', value: 'Hasta 8 MB expandible' }
  ];

  const optionalFeatures = [
    'Rechazador neumático',
    'Semáforo de sobre/bajo peso',
    'Sistemas de alarma',
    'Aplicación automática de etiquetas',
    'Impresión de códigos de barras',
    'Escaneo de códigos de barras',
    'Indicadores WI-127',
    'Indicador Programable 1310'
  ];

  const applications = [
    {
      title: 'Industria Alimentaria',
      description: 'Control de peso en empaque de alimentos, verificación de porciones y cumplimiento de estándares.',
    },
    {
      title: 'Farmacéutica',
      description: 'Verificación de peso de medicamentos empacados y control de calidad en producción.',
    },
    {
      title: 'Manufactura',
      description: 'Control de peso de componentes y productos terminados en líneas de ensamble.',
    },
    {
      title: 'Logística',
      description: 'Verificación de peso de paquetes en centros de distribución y clasificación.',
    },
  ];

  const advantages = [
    {
      title: 'Alta Velocidad',
      description: 'Procesamiento de hasta 20 productos por minuto sin comprometer la precisión.',
    },
    {
      title: 'Flexibilidad',
      description: 'Adaptables a transportadores existentes con kits de conversión.',
    },
    {
      title: 'Automatización',
      description: 'Sistemas de rechazo automático y control de calidad integrado.',
    },
    {
      title: 'Durabilidad',
      description: 'Construcción robusta para operación continua en entornos industriales.',
    },
  ];

  return (
    <div className="bls-page">
      {error && <div className="bls-status-bar bls-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="bls-hero">
        <div className="container bls-hero-content">
          <nav className="bls-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="bls-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="bls-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas de Banda</span>
          </nav>
          <h1 className="bls-hero-title">Básculas de Banda</h1>
          <p className="bls-hero-subtitle">
            Sistemas de pesaje dinámico para transportadores y líneas de producción industrial
          </p>
          {authType === 'internal' && (
            <button
              className={`bls-btn-edit-mode${editMode ? ' bls-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="bls-hero-image" aria-hidden="true">
          <img src="/images/conveyor-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="bls-overview">
        <div className="container">
          <div className="bls-overview-grid">
            <div className="bls-overview-text">
              <span className="bls-label">Descripción general</span>
              <h2>Verificación de peso en movimiento</h2>
              <p>
                Nuestras básculas de banda están diseñadas para proporcionar verificación de peso
                precisa en movimiento, optimizando el control de calidad en líneas de producción
                de alta velocidad. Ofrecemos soluciones personalizables para diversas aplicaciones
                industriales.
              </p>
              
            </div>
            <div className="bls-overview-image">
              <ul className="bls-features-list" aria-label="Características principales">
                <li>
                  <svg className="bls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Verificación de peso en movimiento de alta precisión
                </li>
                <li>
                  <svg className="bls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Construcción robusta en acero inoxidable o al carbón
                </li>
                <li>
                  <svg className="bls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Velocidades variables para diferentes aplicaciones
                </li>
                <li>
                  <svg className="bls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Sistemas de rechazo automático
                </li>
                <li>
                  <svg className="bls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Integración con líneas de producción existentes
                </li>
                <li>
                  <svg className="bls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Cumplimiento con estándares USDA
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="bls-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos de Básculas de Banda</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/belt-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed ?? false}
                  endOfLife={model.endOfLife}
                  productId={model.productId ?? ''}
                  name={model.name ?? ''}
                  badge={model.specs?.model ?? undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image ?? undefined}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.conveyorWidths?.length ? [{ icon: '↔️', label: 'Anchos de transportador', items: (model.specs.conveyorWidths ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.features?.length       ? [{ icon: '⚙️', label: 'Características',         items: (model.specs.features ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.capacities?.length     ? [{ icon: '📦', label: 'Capacidades',              items: (model.specs.capacities ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Technical specifications ──────────────────────────── */}
      <section className="bls-specifications">
        <div className="container">
          <span className="bls-label">Datos técnicos</span>
          <h2 className="bls-section-title">Especificaciones técnicas</h2>
          <div className="bls-specs-table-wrap">
            <table className="bls-specs-table" aria-label="Especificaciones técnicas de básculas de banda">
              <tbody>
                {specifications.map((spec, index) => (
                  <tr key={index}>
                    <td className="bls-specs-label">{spec.label}</td>
                    <td className="bls-specs-value">{spec.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="bls-applications">
        <div className="container">
          <span className="bls-label bls-label--light">Industrias</span>
          <h2 className="bls-section-title bls-section-title--light">Aplicaciones</h2>
          <div className="bls-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="bls-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Optional features ────────────────────────────────── */}
      <section className="bls-configurations">
        <div className="container">
          <div className="bls-configurations-inner">
            <div className="bls-configurations-header">
              <span className="bls-label">Personalización</span>
              <h2 className="bls-section-title">Características opcionales</h2>
              <p>
                Adapte su báscula de banda a los requerimientos específicos
                de su línea de producción.
              </p>
            </div>
            <ul className="bls-config-list" aria-label="Características opcionales">
              {optionalFeatures.map((feature, index) => (
                <li key={index} className="bls-config-item">
                  <svg className="bls-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Advantages ───────────────────────────────────────── */}
      <section className="bls-advantages">
        <div className="container">
          <span className="bls-label">Beneficios</span>
          <h2 className="bls-section-title">Ventajas de nuestras básculas de banda</h2>
          <div className="bls-advantages-grid">
            {advantages.map((adv, index) => (
              <div key={index} className="bls-adv-card">
                <h3>{adv.title}</h3>
                <p>{adv.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="bls-cta">
        <div className="container">
          <div className="bls-cta-inner">
            <div className="bls-cta-text">
              <span className="bls-label bls-label--light">Solicite información</span>
              <h2 className="bls-cta-title">¿Necesita una báscula de banda?</h2>
              <p>
                Nuestros especialistas pueden diseñar la solución perfecta para su línea de
                producción y ayudarle a optimizar su control de calidad.
              </p>
            </div>
            <div className="bls-cta-actions">
              <Link to="/contacto" className="bls-btn-primary">Solicitar cotización</Link>
              <Link to="/servicios" className="bls-btn-secondary">Ver servicios</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BeltScales;
